import { HoraParadaAcoes } from "./HoraParadaAcoes";
import { createElement, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Bell,
  BellOff,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  Circle,
  Clock,
  Copy,
  Hourglass,
  Inbox,
  Loader2,
  Mail,
  Settings2,
  Undo2,
  UserCheck,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { toast } from "../ui/sonner";
import { destinoDoServico } from "../../utils/serviceLink";
import api from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import { subscribeAdminWS } from "../../lib/adminWS";
import {
  cancelSnooze,
  getAudioState,
  playOnce,
  release,
  releaseAll,
  snooze,
  SNOOZE_OPTIONS,
  subscribeAudioState,
  sustain,
  syncSnoozeUntil,
  syncSustained,
} from "../../lib/notificationSound";
import {
  CATEGORY_ICON,
  eventIcon,
  severityColor,
} from "../../lib/notificationIcons";
import { accentBg } from "../../utils/accent";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

const PAGE_SIZE = 20;

// Tempo restante do silenciamento: minutos até 1h, depois horas.
const restanteSnooze = (until) => {
  const min = Math.max(1, Math.round((until - Date.now()) / 60000));
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  return `${h} hora${h > 1 ? "s" : ""}`;
};


const SEVERITY_DOT = {
  critical: "bg-red-500",
  warning: "bg-amber-500",
  info: "bg-sky-500",
};

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const hhmm = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return hhmm;
  return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${hhmm}`;
}

const firstName = (n) => (n || "").trim().split(/\s+/)[0];

/** ISO do servidor → timestamp; vazio/passado vira 0 (sem silenciamento). */
const estadoParaTs = (iso) => {
  if (!iso) return 0;
  const ts = new Date(iso).getTime();
  return Number.isFinite(ts) && ts > Date.now() ? ts : 0;
};

/** Tooltip que não muda o cursor e funciona dentro do drawer. */
const Hint = ({ text, children }) => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent
      side="top"
      // Os toasts do Sonner usam z-index gigante; sem isso o tooltip fica atrás.
      style={{ zIndex: 2147483600 }}
      className="max-w-[280px] whitespace-pre-line leading-snug"
    >
      {text}
    </TooltipContent>
  </Tooltip>
);

/** Conteúdo enviado pelo app na validação de chegada (placa, telefone...). */
function validationValue(n) {
  if (n.event_key !== "service.arrival_validation_pending") return null;
  const fromPayload = n.payload?.validation_value;
  if (fromPayload) return String(fromPayload);
  const m = /^[^:]{2,20}:\s*(.+)$/.exec(n.body || "");
  return m ? m[1].trim() : null;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const location = useLocation();
  // No Painel Logístico a OS abre ali mesmo, em vez de jogar o operador para a
  // listagem de serviços.
  // Notificação de mensagem sem serviço abre a gaveta de chat na conversa —
  // e isso funciona em qualquer tela (o botão de chat vive no header), então
  // mantém o operador onde ele está em vez de jogá-lo para a listagem.
  const destino = (link) => {
    if (link.includes("chat_thread=")) {
      return `${location.pathname}${link.slice(link.indexOf("?"))}`;
    }
    return destinoDoServico(link, location.pathname);
  };
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [onlyUnread, setOnlyUnread] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [audio, setAudio] = useState(getAudioState());
  const catsLoaded = useRef(false);
  // O mesmo `notification:new` pode chegar mais de uma vez (várias abas abertas
  // ou conexão WS antiga ainda registrada na sala). Ignoramos repetições.
  const seenIds = useRef(new Set());
  // Toasts que ficam na tela até alguém fechar (avisos críticos): guardamos o
  // que é preciso para redesenhá-los quando o texto muda no servidor.
  const toastsFixos = useRef(new Map());
  const snoozeActive = audio.snoozeUntil > Date.now();

  const loadPage = useCallback(
    async ({ reset = false, unreadOnly = false } = {}) => {
      setLoading(true);
      try {
        const skip = reset ? 0 : items.length;
        const { data } = await api.get("/notifications/inbox", {
          params: { limit: PAGE_SIZE, skip, unread: unreadOnly },
        });
        const page = data?.items || [];
        setItems((prev) => (reset ? page : [...prev, ...page]));
        setUnread(data?.unread_count || 0);
        setHasMore(page.length === PAGE_SIZE);
      } catch {
        /* silencioso: o sino não deve quebrar o header */
      } finally {
        setLoading(false);
      }
    },
    [items.length],
  );

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data?.unread_count || 0);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  useEffect(() => subscribeAudioState(setAudio), []);

  // Silenciamento e "parar alarmes" ficam salvos NO USUÁRIO: ao abrir ou
  // atualizar a página buscamos o estado do servidor, e as outras abas/
  // dispositivos são avisados por WebSocket.
  useEffect(() => {
    let vivo = true;
    api
      .get("/notifications/sound-state")
      .then(({ data }) => {
        if (vivo) syncSnoozeUntil(estadoParaTs(data?.muted_until));
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [user?.id]);

  useEffect(() => {
    return subscribeAdminWS("notification:sound_state", (d) => {
      syncSnoozeUntil(estadoParaTs(d?.muted_until));
      if (d?.alarms_stopped_at) releaseAll();
    });
  }, []);

  // Atualiza o contador do snooze na tela.
  useEffect(() => {
    if (!snoozeActive) return;
    const t = setInterval(() => setAudio(getAudioState()), 30000);
    return () => clearInterval(t);
  }, [snoozeActive]);

  // Mantém os alarmes em sincronia com o servidor: retoma o que está aberto
  // (ex: após F5) e derruba o que já foi resolvido em outro lugar — sem
  // depender do WebSocket, que pode cair em abas em segundo plano.
  const syncAlarms = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/inbox", {
        params: { limit: 100, unread: true },
      });
      const agora = Date.now();
      const open = (data?.items || []).filter(
        (n) =>
          n.sound_persistent &&
          !n.resolved_at &&
          // Em soneca: quem deu o ciente não ouve o alarme até o prazo vencer.
          !(n.snoozed_until && new Date(n.snoozed_until).getTime() > agora),
      );
      open.forEach((n) => sustain(n.id, n.sound));
      syncSustained(open.map((n) => n.id));
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    syncAlarms();
    const timer = setInterval(syncAlarms, 60000);
    const onFocus = () => syncAlarms();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [syncAlarms]);

  /** Ciente: some da tela e cala o aviso pelo tempo configurado. */
  const darCiente = useCallback(async (d) => {
    const minutos = Number(d?.snooze_min || 0);
    if (!minutos) return;
    release(d.id);
    try {
      await api.post(`/notifications/inbox/${d.id}/snooze`);
      setItems((prev) =>
        prev.map((i) =>
          i.id === d.id
            ? { ...i, snoozed_until: new Date(Date.now() + minutos * 60000).toISOString() }
            : i,
        ),
      );
    } catch {
      /* noop */
    }
  }, []);

  /** Mostra (ou redesenha) o toast de um aviso. */
  const mostrarToast = useCallback((d) => {
    const Icon = eventIcon(d.event_key);
    const color = severityColor(d.severity);
    const secs = Number(d.toast_sec ?? 5);
    const fixo = secs <= 0;
    if (fixo) toastsFixos.current.set(d.id, d);
    toast(d.title, {
      description:
        d.event_key === "service.hora_parada" && d.payload?.service_id && !d.resolved_at
          ? createElement(
              "div",
              null,
              d.body,
              createElement(HoraParadaAcoes, {
                serviceId: d.payload.service_id,
                onFeito: () => {
                  toastsFixos.current.delete(d.id);
                  toast.dismiss(`notif-${d.id}`);
                },
              }),
            )
          : d.body || undefined,
      id: `notif-${d.id}`,
      icon: createElement(Icon, { size: 18, strokeWidth: 1.5, color }),
      style: { borderLeft: `4px solid ${color}` },
      // 0 = fica na tela até a pessoa fechar
      duration: fixo ? Infinity : secs * 1000,
      closeButton: fixo,
      // Ciente: só o ícone, discreto, com explicação no passar do mouse —
      // o aviso já é grande o bastante sem mais um botão colorido.
      action:
        fixo && Number(d.snooze_min || 0) > 0
          ? {
              label: createElement(
                Hint,
                { text: `Ciente — silencia este aviso por ${d.snooze_min} min` },
                // O Sonner monta o <button> dele e põe isso dentro; o gatilho
                // ocupa o botão inteiro para o tooltip abrir em qualquer ponto.
                createElement(
                  "span",
                  {
                    style: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    },
                  },
                  createElement(Hourglass, { size: 14, strokeWidth: 2 }),
                ),
              ),
              onClick: () => darCiente(d),
            }
          : undefined,
      actionButtonStyle: {
        background: "#fff",
        color: "#6b7280",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        boxShadow: "none",
        padding: "0",
        height: "26px",
        width: "26px",
        minWidth: "26px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      // Fechado pela pessoa: não redesenha mais (senão o aviso voltaria) e,
      // se o evento assim manda, fechar já vale como ciente.
      onDismiss: () => {
        toastsFixos.current.delete(d.id);
        if (d.close_is_ack !== false) darCiente(d);
      },
      onAutoClose: () => toastsFixos.current.delete(d.id),
    });
  }, [darCiente]);

  // O protocolo externo pode ser preenchido depois do aviso: o backend
  // reescreve o título e avisa por aqui pra lista refletir na hora. O toast
  // crítico fica na tela até alguém fechar, então ele também é redesenhado —
  // senão continuaria mostrando o protocolo antigo por horas.
  useEffect(() => {
    return subscribeAdminWS("notification:updated", (d) => {
      const byId = {};
      for (const it of d?.items || []) byId[it.id] = it.title;
      setItems((prev) => prev.map((i) => (byId[i.id] ? { ...i, title: byId[i.id] } : i)));
      for (const [id, titulo] of Object.entries(byId)) {
        const aberto = toastsFixos.current.get(id);
        if (aberto) mostrarToast({ ...aberto, title: titulo });
      }
    });
  }, [mostrarToast]);

  // Clique no selo "Aviso de hora parada" reabre o aviso do serviço.
  useEffect(() => {
    const abrir = (e) => {
      const sid = e.detail?.serviceId;
      const eh = (d) => d?.event_key === "service.hora_parada" && d?.payload?.service_id === sid && !d?.resolved_at;
      const d = [...toastsFixos.current.values()].find(eh) || items.find(eh);
      if (!d) return;
      e.detail.handled = true;
      mostrarToast({ ...d, toast_sec: 0 });
    };
    window.addEventListener("hora-parada:mostrar", abrir);
    return () => window.removeEventListener("hora-parada:mostrar", abrir);
  }, [items, mostrarToast]);



  useEffect(() => {
    return subscribeAdminWS("notification:resolved", (d) => {
      const ids = d?.ids || [];
      ids.forEach((id) => release(id));
      setItems((prev) =>
        prev.map((i) =>
          ids.includes(i.id)
            ? {
                ...i,
                resolved_at: d.resolved_at,
                read_at: i.read_at || d.resolved_at,
                resolved_by_name: d.resolved_by_name,
              }
            : i,
        ),
      );
      refreshCount();
    });
  }, [refreshCount]);

  useEffect(() => {
    return subscribeAdminWS("notification:reopened", (d) => {
      const ids = d?.ids || [];
      setItems((prev) =>
        prev.map((i) => {
          if (!ids.includes(i.id)) return i;
          if (i.sound_persistent) sustain(i.id, i.sound);
          return { ...i, resolved_at: null, read_at: null, resolved_by_name: null };
        }),
      );
      refreshCount();
    });
  }, [refreshCount]);

  useEffect(() => {
    return subscribeAdminWS("notification:new", (d) => {
      if (!d || (d.user_id && user?.id && String(d.user_id) !== String(user.id)))
        return;
      if (d.id) {
        if (seenIds.current.has(d.id)) return;
        seenIds.current.add(d.id);
      }
      setItems((prev) => [d, ...prev.filter((i) => i.id !== d.id)]);
      // Contagem sempre vem do servidor — nunca somamos +1 no cliente, senão
      // eventos duplicados inflam o badge.
      refreshCount();
      if (d.sound_persistent && d.resolvable) sustain(d.id, d.sound);
      else playOnce(d.sound);
      // Acionamento do portal tem cartão próprio no canto da tela (com prazo,
      // motorista mais próximo e os botões de decisão): um toast genérico em
      // cima disso seria o mesmo aviso duas vezes.
      if (d.event_key === "portal.detection") return;
      mostrarToast(d);
    });
  }, [user?.id, refreshCount, mostrarToast]);

  async function openDrawer() {
    setOpen(true);
    setOnlyUnread(true);
    setItems([]);
    loadPage({ reset: true, unreadOnly: true });
    if (!catsLoaded.current) {
      catsLoaded.current = true;
      try {
        const { data } = await api.get("/notifications/catalog");
        setCategories(data?.categories || []);
        setEvents(data?.events || []);
      } catch {
        /* noop */
      }
    }
  }

  function switchFilter(unreadOnly) {
    setOnlyUnread(unreadOnly);
    setItems([]);
    loadPage({ reset: true, unreadOnly });
  }

  async function markRead(n) {
    if (n.read_at) return;
    setItems((prev) =>
      prev.map((i) =>
        i.id === n.id ? { ...i, read_at: new Date().toISOString() } : i,
      ),
    );
    setUnread((v) => Math.max(0, v - 1));
    try {
      await api.post(`/notifications/inbox/${n.id}/read`);
    } catch {
      /* noop */
    }
  }

  async function markAll() {
    setItems((prev) =>
      prev.map((i) => ({ ...i, read_at: i.read_at || new Date().toISOString() })),
    );
    setUnread(0);
    try {
      await api.post("/notifications/inbox/read-all");
    } catch {
      /* noop */
    }
  }

  async function toggleResolved(n) {
    if (n.resolved_at) {
      setItems((prev) =>
        prev.map((i) =>
          i.id === n.id
            ? { ...i, resolved_at: null, resolved_by_name: null, read_at: null }
            : i,
        ),
      );
      if (n.sound_persistent) sustain(n.id, n.sound);
      try {
        await api.post(`/notifications/inbox/${n.id}/unresolve`);
      } catch {
        toast.error("Não foi possível reabrir agora");
      }
      return;
    }
    release(n.id);
    try {
      await api.post(`/notifications/inbox/${n.id}/resolve`);
      // O WS `notification:resolved` atualiza a lista (inclusive nas outras abas).
    } catch {
      toast.error("Não foi possível resolver agora");
    }
  }

  const category = (id) => categories.find((c) => c.id === id) || null;

  // Diagnóstico do alarme: qual ocorrência está soando neste dispositivo.
  const playingTitle = (() => {
    const n = items.find((i) => i.id === audio.playingKey);
    if (n) return n.title || n.event_key;
    return audio.playingSound ? `som "${audio.playingSound}"` : "ocorrência antiga";
  })();

  function stopAllAlarms() {
    releaseAll();
    // Fica salvo no usuário: cala o que já estava aberto em todas as abas e
    // dispositivos dele, e continua calado depois de atualizar a página.
    api
      .post("/notifications/sound-state", { action: "stop_alarms" })
      .then(() => toast.success("Alarmes interrompidos para você em todos os dispositivos"))
      .catch(() =>
        toast.warning("Alarmes parados só nesta aba", {
          description: "Não conseguimos salvar no seu usuário agora.",
        }),
      );
  }

  /** Silencia por um tempo — salvo no usuário, não no navegador. */
  function silenciarPor(ms) {
    snooze(ms); // efeito imediato; o servidor confirma em seguida
    api
      .post("/notifications/sound-state", {
        action: "mute",
        minutes: Math.round(ms / 60000),
      })
      .then(({ data }) => syncSnoozeUntil(estadoParaTs(data?.muted_until)))
      .catch(() =>
        toast.warning("Silenciado só neste navegador", {
          description: "Não conseguimos salvar no seu usuário agora.",
        }),
      );
  }

  function reativarSons() {
    cancelSnooze();
    api.post("/notifications/sound-state", { action: "unmute" }).catch(() => {});
  }

  /** Explica no tooltip qual evento gerou o aviso. */
  function originTip(n, cat) {
    const ev = events.find((e) => e.event_key === n.event_key);
    const lines = [`Categoria: ${cat?.label || n.category || "—"}`];
    lines.push(`Evento: ${ev?.label || n.event_key || "—"}`);
    if (ev?.description) lines.push(ev.description);
    return lines.join("\n");
  }

  async function copyValue(value) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copiado", { description: value });
    } catch {
      toast.error("Não foi possível copiar");
    }
  }

  return (
    <TooltipProvider>
      <button
        onClick={openDrawer}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="header-notifications-btn"
        data-tip="Notificações"
      >
        <Bell size={20} className="text-gray-500" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff9945] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:w-[35rem] sm:max-w-none overflow-hidden p-0 gap-0 [&>button:first-child]:hidden shadow-2xl bg-white flex flex-col !z-[1500]"
          overlayClassName="!z-[1499]"
          data-testid="notifications-panel"
        >
          <div
            className="px-6 py-4 text-white flex items-center justify-between shrink-0"
            style={accentBg()}
          >
            <SheetTitle className="flex items-center gap-2 text-white min-w-0">
              <Bell className="h-5 w-5 shrink-0" />
              <span className="truncate">Notificações</span>
              {unread > 0 && (
                <span className="shrink-0 text-[10px] font-semibold bg-white/20 rounded-full pl-[0.8rem] pr-[0.8rem] py-0.5 leading-[2] inline-flex items-center gap-1 whitespace-nowrap">
                  <Mail size={10} />
                  {unread} não lida{unread > 1 ? "s" : ""}
                </span>
              )}
            </SheetTitle>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="notifications-close-btn"
            >
              <X size={20} />
            </button>
          </div>

          <div className="px-4 py-[1.125rem] border-b border-gray-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-1.5">
              {[
                { id: true, label: "Não lidas", Icon: Mail },
                { id: false, label: "Todas", Icon: Inbox },
              ].map((f) => (
                <button
                  key={String(f.id)}
                  type="button"
                  onClick={() => switchFilter(f.id)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                    onlyUnread === f.id
                      ? "border-transparent bg-gray-900 text-white"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid={`notifications-filter-${f.id ? "unread" : "all"}`}
                >
                  <f.Icon size={12} />
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={`p-1.5 rounded-md transition-colors ${
                      snoozeActive
                        ? "text-amber-600 bg-amber-50 hover:bg-amber-100"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    }`}
                    data-tip={snoozeActive ? "Sons silenciados" : "Silenciar sons por um tempo"}
                    data-testid="notifications-snooze-btn"
                  >
                    {snoozeActive ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="z-[1600]">
                  {SNOOZE_OPTIONS.map((o) => (
                    <DropdownMenuItem
                      key={o.ms}
                      onClick={() => silenciarPor(o.ms)}
                      data-testid={`notifications-snooze-${o.ms}`}
                    >
                      <BellOff size={13} className="mr-2" />
                      Silenciar por {o.label}
                    </DropdownMenuItem>
                  ))}
                  {snoozeActive && (
                    <DropdownMenuItem
                      onClick={reativarSons}
                      data-testid="notifications-snooze-cancel"
                    >
                      <Volume2 size={13} className="mr-2" />
                      Reativar os sons agora
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                type="button"
                onClick={markAll}
                disabled={!unread}
                className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors disabled:opacity-40"
                data-tip="Marcar todas como lidas"
                data-testid="notifications-mark-all-btn"
              >
                <CheckCheck size={16} />
              </button>
              <Link
                to="/settings/notifications"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                data-tip="Configurar notificações"
                data-testid="notifications-settings-link"
              >
                <Settings2 size={16} />
              </Link>
            </div>
          </div>

          {audio.blocked && (
            <button
              type="button"
              onClick={() => playOnce("chime")}
              className="w-full px-5 py-2 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-800 flex items-center gap-2 hover:bg-amber-100 transition-colors"
              data-testid="notifications-audio-blocked"
            >
              <Volume2 size={13} />
              O navegador está bloqueando o som. Clique aqui para ativar os avisos sonoros.
            </button>
          )}

          {audio.activeCount > 0 && (
            <div
              className="w-full px-5 py-2 bg-red-50 border-b border-red-100 text-[11px] text-red-800 flex items-center justify-between gap-2"
              data-testid="notifications-active-alarm-banner"
            >
              <span className="flex items-center gap-2 min-w-0">
                <Volume2 size={13} className="shrink-0" />
                <span className="truncate">
                  Alarme tocando: {playingTitle} ({audio.activeCount}{" "}
                  {audio.activeCount === 1 ? "ocorrência" : "ocorrências"} em aberto)
                </span>
              </span>
              <button
                type="button"
                onClick={stopAllAlarms}
                className="shrink-0 font-medium text-red-900 hover:underline"
                data-testid="notifications-stop-alarms"
              >
                Parar alarmes
              </button>
            </div>
          )}

          {snoozeActive && (
            <div
              className="w-full px-5 py-2 bg-gray-100 border-b border-gray-200 text-[11px] text-gray-600 flex items-center justify-between gap-2"
              data-testid="notifications-snooze-banner"
            >
              <span className="flex items-center gap-2">
                <BellOff size={13} />
                Sons silenciados por {restanteSnooze(audio.snoozeUntil)}
              </span>
              <button
                type="button"
                onClick={reativarSons}
                className="font-medium text-gray-800 hover:underline"
                data-testid="notifications-snooze-banner-cancel"
              >
                Reativar
              </button>
            </div>
          )}

          {!snoozeActive && audio.waitingCount > 0 && (
            <div
              className="w-full px-5 py-2 bg-sky-50 border-b border-sky-100 text-[11px] text-sky-800 flex items-center gap-2"
              data-testid="notifications-audio-queue"
            >
              <Volume2 size={13} />
              Um alerta por vez: {audio.waitingCount} aviso
              {audio.waitingCount > 1 ? "s" : ""} em espera, soa quando o atual for
              resolvido.
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {loading && !items.length ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={20} className="animate-spin text-gray-400" />
              </div>
            ) : !items.length ? (
              <div
                className="flex flex-col items-center justify-center gap-2 py-20 text-gray-400"
                data-testid="notifications-empty"
              >
                <Inbox size={28} />
                <p className="text-xs">
                  {onlyUnread
                    ? "Nenhuma notificação não lida."
                    : "Nenhuma notificação por aqui."}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {items.map((n) => {
                  const cat = category(n.category);
                  const CatIcon = (cat && CATEGORY_ICON[cat.icon]) || Circle;
                  const soundOn =
                    n.sound_persistent && !n.resolved_at && !snoozeActive;
                  const copyable = validationValue(n);
                  const content = (
                    <>
                      <span
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          SEVERITY_DOT[n.severity] || SEVERITY_DOT.info
                        } ${n.read_at ? "opacity-30" : ""}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block text-sm leading-snug ${
                            n.read_at ? "text-gray-500" : "font-medium text-gray-800"
                          }`}
                        >
                          {n.title}
                        </span>
                        {n.body ? (
                          <span className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-xs text-gray-500 leading-snug">
                              {n.body}
                            </span>
                            {copyable ? (
                              <Hint text={`Copiar ${copyable}`}>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyValue(copyable);
                                  }}
                                  className="shrink-0 h-5 w-5 rounded border border-gray-200 bg-white flex items-center justify-center text-gray-400 hover:text-gray-800 hover:border-gray-300 transition-colors"
                                  data-testid={`notification-copy-${n.id}`}
                                >
                                  <Copy size={11} />
                                </span>
                              </Hint>
                            ) : null}
                          </span>
                        ) : null}
                        {n.event_key === "service.hora_parada" && n.payload?.service_id && !n.resolved_at ? (
                          <HoraParadaAcoes serviceId={n.payload.service_id} onFeito={() => { loadPage({ reset: true }); refreshCount(); }} />
                        ) : null}
                        <span className="flex items-center gap-2 text-[11px] text-gray-400 mt-1.5 flex-wrap">
                          {cat ? (
                            <Hint text={originTip(n, cat)}>
                              <span
                                className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 inline-flex items-center gap-1"
                                data-testid={`notification-category-${n.id}`}
                              >
                                <CatIcon size={10} />
                                {cat.label}
                              </span>
                            </Hint>
                          ) : null}
                          <Hint text={new Date(n.created_at).toLocaleString("pt-BR")}>
                            <span className="inline-flex items-center gap-1">
                              <Clock size={10} />
                              {timeLabel(n.created_at)}
                            </span>
                          </Hint>
                          {n.snoozed_until &&
                          new Date(n.snoozed_until) > new Date() &&
                          !n.resolved_at ? (
                            <span
                              className="inline-flex items-center gap-1 text-amber-700"
                              data-testid={`notification-snoozed-${n.id}`}
                            >
                              <Hourglass size={10} />
                              em soneca até{" "}
                              {new Date(n.snoozed_until).toLocaleTimeString("pt-BR", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          ) : null}
                          {n.resolved_at ? (
                            <Hint
                              text={`Resolvido${
                                n.resolved_by_name ? ` por ${n.resolved_by_name}` : ""
                              } em ${new Date(n.resolved_at).toLocaleString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`}
                            >
                              <span
                                className="inline-flex items-center gap-1 text-emerald-600"
                                data-testid={`notification-resolved-by-${n.id}`}
                              >
                                <UserCheck size={10} />
                                {firstName(n.resolved_by_name) || "Resolvido"}
                              </span>
                            </Hint>
                          ) : null}
                          {soundOn ? (
                            audio.playingKey === n.id ? (
                              <span
                                className="inline-flex items-center gap-1 text-red-600 animate-pulse"
                                data-testid={`notification-sound-playing-${n.id}`}
                              >
                                <Volume2 size={10} />
                                soando
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1"
                                data-testid={`notification-sound-waiting-${n.id}`}
                              >
                                <VolumeX size={10} />
                                em espera
                              </span>
                            )
                          ) : null}
                        </span>
                      </span>
                    </>
                  );
                  return (
                    <li
                      key={n.id}
                      className="flex items-stretch hover:bg-gray-50 transition-colors"
                      data-testid={`notification-item-${n.id}`}
                    >
                      {n.link ? (
                        <Link
                          to={destino(n.link)}
                          onClick={() => {
                            markRead(n);
                            setOpen(false);
                          }}
                          className="flex gap-3 flex-1 min-w-0 pl-5 pr-2 py-3.5"
                        >
                          {content}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => markRead(n)}
                          className="text-left flex gap-3 flex-1 min-w-0 pl-5 pr-2 py-3.5"
                        >
                          {content}
                        </button>
                      )}
                      {n.resolvable && (
                        <Hint
                          text={
                            n.resolved_at
                              ? "Desmarcar como resolvida"
                              : "Marcar como resolvida"
                          }
                        >
                          <button
                            type="button"
                            onClick={() => toggleResolved(n)}
                            className={`shrink-0 mr-3 self-center h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
                              n.resolved_at
                                ? "text-gray-400 bg-gray-100 hover:bg-gray-200 hover:text-gray-600"
                                : "text-gray-300 hover:text-emerald-600 hover:bg-emerald-50"
                            }`}
                            data-testid={`notification-resolve-${n.id}`}
                          >
                            {n.resolved_at ? (
                              <Undo2 size={17} />
                            ) : (
                              <CheckCircle2 size={20} />
                            )}
                          </button>
                        </Hint>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {hasMore && (
              <div className="px-5 py-4">
                <button
                  type="button"
                  onClick={() => loadPage({ unreadOnly: onlyUnread })}
                  disabled={loading}
                  className="w-full h-9 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                  data-testid="notifications-load-more"
                >
                  {loading ? (
                    <>
                      <Loader2 size={13} className="animate-spin" />
                      Carregando...
                    </>
                  ) : (
                    <>
                      <ChevronDown size={13} />
                      Carregar mais
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 px-5 py-3 shrink-0">
            <Link
              to="/settings/notifications"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-gray-600 hover:text-gray-900"
              data-testid="notifications-settings-link"
            >
              Configurar meus avisos →
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
}
